import { z } from 'zod';

export const StorageCategory = z.enum(['content', 'media', 'ai_artifact', 'private_asset']);
export type StorageCategory = z.infer<typeof StorageCategory>;

export const StorageEnforcementMode = z.enum(['observe', 'enforce']);
export type StorageEnforcementMode = z.infer<typeof StorageEnforcementMode>;

export const StorageCategoryBytes = z.object({
  content: z.number().int().nonnegative(),
  media: z.number().int().nonnegative(),
  ai_artifact: z.number().int().nonnegative(),
  private_asset: z.number().int().nonnegative(),
});
export type StorageCategoryBytes = z.infer<typeof StorageCategoryBytes>;

export const TenantStorageStatus = z.object({
  used_bytes: z.number().int().nonnegative(),
  reserved_bytes: z.number().int().nonnegative(),
  limit_bytes: z.number().int().min(-1),
  usage_percent: z.number().nonnegative().nullable(),
  threshold: z.enum(['normal', 'warning', 'exceeded']),
  category_bytes: StorageCategoryBytes,
  enforcement_mode: StorageEnforcementMode,
  hypothetical_exceeded: z.boolean(),
  reconciliation_status: z.enum(['clean', 'pending', 'drifted']),
  updated_at: z.string().nullable(),
});
export type TenantStorageStatus = z.infer<typeof TenantStorageStatus>;

export const AdminStorageGateCheck = z.object({
  key: z.enum([
    'full_calendar_month',
    'backfill_completed',
    'reconciliation_clean',
    'unknown_objects_resolved',
    'overage_notifications_complete',
    'grace_window_elapsed',
  ]),
  passed: z.boolean(),
  detail: z.string(),
});
export type AdminStorageGateCheck = z.infer<typeof AdminStorageGateCheck>;

export const AdminStorageRuntimeStatus = z.object({
  mode: StorageEnforcementMode,
  observe_started_at: z.string(),
  version: z.number().int().positive(),
  updated_at: z.string(),
  tenant_count: z.number().int().nonnegative(),
  warning_tenant_count: z.number().int().nonnegative(),
  exceeded_tenant_count: z.number().int().nonnegative(),
  hypothetical_exceeded_operation_count: z.number().int().nonnegative(),
  reconcile_required_operation_count: z.number().int().nonnegative(),
  unknown_object_count: z.number().int().nonnegative(),
  latest_backfill: z
    .object({
      id: z.string(),
      status: z.enum(['running', 'completed', 'failed']),
      started_at: z.string(),
      completed_at: z.string().nullable(),
      scanned_count: z.number().int().nonnegative(),
      unknown_count: z.number().int().nonnegative(),
      missing_count: z.number().int().nonnegative(),
    })
    .nullable(),
  checks: z.array(AdminStorageGateCheck),
  can_enable: z.boolean(),
  state_token: z.string(),
});
export type AdminStorageRuntimeStatus = z.infer<typeof AdminStorageRuntimeStatus>;

export const AdminStorageModeCommitRequest = z
  .object({
    target_mode: StorageEnforcementMode,
    reason: z.string().trim().min(1).max(500),
    state_token: z.string().min(1).max(256),
    idempotency_key: z.string().min(1).max(128),
  })
  .strict();
export type AdminStorageModeCommitRequest = z.infer<typeof AdminStorageModeCommitRequest>;

export const AdminStorageModeCommitResponse = z.object({
  status: AdminStorageRuntimeStatus,
  replayed: z.boolean(),
});
export type AdminStorageModeCommitResponse = z.infer<typeof AdminStorageModeCommitResponse>;

export const AdminStorageOverageNotificationRequest = z
  .object({
    idempotency_key: z.string().min(1).max(128),
    channel: z.enum(['in_app', 'email', 'manual']),
    status: z.enum(['recorded', 'sent', 'failed']),
    notified_at: z.string().datetime(),
    grace_deadline: z.string().datetime(),
  })
  .strict()
  .refine((value) => value.grace_deadline > value.notified_at, {
    path: ['grace_deadline'],
    message: 'Grace deadline must be after notification time',
  });
export type AdminStorageOverageNotificationRequest = z.infer<
  typeof AdminStorageOverageNotificationRequest
>;
